// AI team-analysis service.
// The browser calls our OWN backend at /api/analyzeTeam, which holds the
// Anthropic API key server-side (Azure Function in prod, local-proxy.js in dev).
// The key is never exposed to the browser.

const CI_SYSTEM_PROMPT = `You are a seasoned organizational consultant with deep Culture Index expertise, briefing a construction executive in person. You know the framework cold:

TRAITS:
- A (Autonomy): Higher = assertive, visionary, risk-taker, headstrong, wants control. Lower = accommodating, cooperative, diplomatic, avoids conflict, team player not leader.
- B (Social Ability): Higher = extroverted, persuasive, relationship-driven, talks through problems, energized by people. Lower = introverted, analytical, factual, prefers working alone, skeptical.
- C (Pace): Higher = methodical, patient, single-focus, dislikes change/pressure, creature of habit. Lower = fast-paced, multi-tasker, impatient, variety-driven, sense of urgency.
- D (Conformity): Higher = rule-follower, detail-oriented, precise, self-disciplined, worrier, quality control. Lower = rule-bender, conceptual, delegates details, fearless, informal.
- EU (Energy Units): 11-30 = needs recharge time. 31-60 = average stamina. 61-89 = tireless focus.
- L (Logic): 0-2 = emotional, wears heart on sleeve. 3-7 = normative blend. 8-10 = pure logic, hard to read emotionally.
- I (Ingenuity): 0-6 = literal, linear thinker. 7-10 = abstract, unconventional, outside the box.

CONSTRUCTION CONTEXT:
- PMs need high A (ownership) and moderate D (track details without micromanaging).
- Superintendents need high C (methodical, single focus) and moderate-high D (safety, quality, procedures).
- The PM/Super relationship is the CORE pairing — most project friction lives there.
- PXs oversee multiple projects and need high A and B.
- A fast PM (low C) + steady Super (high C) is the IDEAL combo — the PM drives urgency, the Super holds the line.
- Two high-A people on the same team can create power struggles.
- High-B PM + low-B Super = communication-style clash (talk it out vs. give me facts).

When analyzing, lead with the PM/Super relationship, watch for very high A competing with the PM for control, check there's enough D for quality/safety, watch energy balance, and weigh the "gas / brake / glue" mix (accelerators with high A+low C, stabilizers with high D+high C, connectors with high B).

VOICE — this matters:
- Write like a sharp human advisor talking to a builder, not like an AI report. Warm, direct, concrete, specific to a job site.
- NO markdown formatting of any kind inside the analysis: no asterisks, no bold, no italics, no bullet points, no numbered lists.
- Refer to people by their plain names (write "Victor Pa", never "**Victor**").
- Vary your sentence structure. Make a real point in every sentence. No corporate filler, no hedging, no "in conclusion."`;

// token = the MSAL id_token (useData().getToken()); the backend requires sign-in.
export async function generateTeamNarrative(teamMembers, peopleData, token) {
  const teamProfiles = teamMembers.map((member) => {
    const person = peopleData.find((p) => p.FullName === member.PersonName);
    if (!person) return `${member.ProjectRole}: ${member.PersonName} (no CI profile loaded)`;
    return `${member.ProjectRole}: ${member.PersonName}
  Archetype: ${person.Archetype || "Unknown"}
  Survey Traits: A=${person.Survey_A}, B=${person.Survey_B}, C=${person.Survey_C}, D=${person.Survey_D}, EU=${person.Survey_EU}, L=${person.Survey_L}, I=${person.Survey_I}
  Job Behaviors: A=${person.Job_A}, B=${person.Job_B}, C=${person.Job_C}, D=${person.Job_D}, EU=${person.Job_EU}`;
  }).join("\n\n");

  const prompt = `Analyze this construction project team. Write it as a flowing, executive-ready briefing — substantive and specific, the kind a trusted advisor would hand a project executive.

Use exactly these four section headers, each on its own line, and nothing else as a header:
## TEAM TRIUMPHS
## TEAM STRUGGLES
## LEADERSHIP GUIDANCE
## COMPATIBILITY SCORE

Within TEAM TRIUMPHS and TEAM STRUGGLES, write 2-3 tight paragraphs of natural prose — name the specific people and pairings and explain exactly how their traits play out on the job site. Within LEADERSHIP GUIDANCE, write one focused paragraph of concrete, actionable advice for the PX. After the COMPATIBILITY SCORE header, put a single number from 1-100 and nothing else.

Remember: no markdown inside the prose — no asterisks, bold, or bullets. Plain names only.

TEAM PROFILES:
${teamProfiles}`;

  const response = await fetch("/api/analyzeTeam", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Delphi-Token": token } : {}) },
    body: JSON.stringify({ system: CI_SYSTEM_PROMPT, prompt, max_tokens: 1600 }),
  });

  if (!response.ok) {
    let msg = `Analysis request failed (${response.status}).`;
    try { const e = await response.json(); if (e.error) msg = e.error; } catch {}
    throw new Error(msg);
  }

  const data = await response.json();
  const text = data.text || "";
  const scoreMatch = text.match(/COMPATIBILITY SCORE[:\s]*(\d+)/i);
  const score = scoreMatch ? parseInt(scoreMatch[1]) : null;
  return { narrative: text, score };
}

// Strip any stray markdown so the rendered text always reads clean.
export function cleanProse(txt) {
  return (txt || "")
    .replace(/\*\*/g, "")
    .replace(/(^|\s)\*(\S)/g, "$1$2")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/^\s*#+\s*/gm, "")
    .trim();
}

export function parseNarrativeSections(text) {
  const sections = { triumphs: "", struggles: "", guidance: "", score: null };
  const t = text.match(/##\s*TEAM TRIUMPHS([\s\S]*?)(?=##\s*TEAM STRUGGLES|$)/i);
  const s = text.match(/##\s*TEAM STRUGGLES([\s\S]*?)(?=##\s*LEADERSHIP GUIDANCE|$)/i);
  const g = text.match(/##\s*LEADERSHIP GUIDANCE([\s\S]*?)(?=##\s*COMPATIBILITY SCORE|$)/i);
  const sc = text.match(/COMPATIBILITY SCORE[:\s]*(\d+)/i);
  if (t) sections.triumphs = cleanProse(t[1]);
  if (s) sections.struggles = cleanProse(s[1]);
  if (g) sections.guidance = cleanProse(g[1]);
  if (sc) sections.score = parseInt(sc[1]);
  return sections;
}